import { useSlideEditorContext } from "@/components/slides/editor/SlideEditorContext";
import {
  BulkMoveToTrashMediaHouseDocument,
  type BulkMoveToTrashMediaHouseMutation,
  type BulkMoveToTrashMediaHouseMutationVariables,
  GetMediaHouseVideosDocument,
  type MediaEdge,
  type MediaHouseMedia,
} from "@/generated/graphql";
import { Dnd } from "@/lib/dnd/components/Dnd";
import { useMediaHandler } from "@/lib/hooks/slides/editor/useMediaHandler";
import { Button } from "@ai-teach/ui-web";
import { useMutation, useQuery } from "@apollo/client/react";
import { Dialog, DialogPanel } from "@headlessui/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  FiAlertTriangle,
  FiCheck,
  FiFilm,
  FiPlay,
  FiRefreshCw,
  FiUploadCloud,
  FiX,
} from "react-icons/fi";
import { DisabledLockOverlay } from "../DisabledLockOverlay";
import { MediaPropertiesDialog } from "./modals/MediaPropertiesDialog";
import { MediaUploadModal } from "./modals/MediaUploadModal";
import { MoveMediaModal } from "./modals/MoveMediaModal";
import { RenameMediaModal } from "./modals/RenameMediaModal";
import { FoldersSection } from "./sections/FoldersSection";
import { FloatingActionBar } from "./shared/FloatingActionBar";
import { ThreeDotMenu } from "./shared/ThreeDotMenu";

const isServerError = (err: unknown): boolean => {
  if (!err || typeof err !== "object") return false;
  const error = err as { networkError?: unknown; graphQLErrors?: unknown[] };
  if (error.networkError) return true;
  const serverErrorCodes = ["INTERNAL_SERVER_ERROR", "SERVICE_UNAVAILABLE"];
  return (
    error.graphQLErrors?.some((e: unknown) => {
      const graphQLError = e as { extensions?: { code?: string } };
      return serverErrorCodes.includes(graphQLError.extensions?.code as string);
    }) ?? false
  );
};

const VideoPreviewModal = ({
  isOpen,
  onClose,
  media,
}: {
  isOpen: boolean;
  onClose: () => void;
  media: MediaHouseMedia | null;
}) => {
  if (!media) return null;
  return (
    <Dialog as="div" className="relative z-[910]" onClose={onClose} open={isOpen}>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-sm" />
      <div className="fixed inset-0 overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel className="w-full max-w-4xl overflow-hidden rounded-2xl bg-black shadow-2xl border border-white/10">
            <div className="flex items-center justify-between px-4 py-3 bg-gray-900/80 backdrop-blur-sm border-b border-white/10">
              <h3 className="text-sm font-medium text-white truncate max-w-md">{media.filename}</h3>
              <button
                type="button"
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
              >
                <FiX className="h-4 w-4" />
              </button>
            </div>
            <div className="relative aspect-video bg-black">
              <video src={media.url} controls autoPlay className="w-full h-full object-contain">
                <track kind="captions" />
              </video>
            </div>
            {media.duration && (
              <div className="bg-gray-900/80 px-4 py-2 border-t border-white/10">
                <span className="text-xs text-gray-400">{Math.round(media.duration)}s</span>
              </div>
            )}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
};

interface VideoTabProps {
  disabled?: boolean;
}

export function VideoTab({ disabled = false }: VideoTabProps) {
  const { setActivePanel } = useSlideEditorContext();
  const [currentFolderId, setCurrentFolderId] = useState<string | null>(null);
  const [selectedMedia, setSelectedMedia] = useState<Map<string, MediaHouseMedia>>(new Map());
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [previewMedia, setPreviewMedia] = useState<MediaHouseMedia | null>(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [showPropertiesDialog, setShowPropertiesDialog] = useState(false);
  const [activeMedia, setActiveMedia] = useState<MediaHouseMedia | null>(null);
  const [itemsToMove, setItemsToMove] = useState<MediaHouseMedia[]>([]);

  const observerTarget = useRef<HTMLDivElement>(null);
  const { addVideoToSlide, copying } = useMediaHandler();

  const { data, loading, error, fetchMore, refetch } = useQuery(GetMediaHouseVideosDocument, {
    variables: { folderId: currentFolderId, first: 20 },
    fetchPolicy: "cache-and-network",
    skip: disabled,
  });

  const [bulkDelete] = useMutation<
    BulkMoveToTrashMediaHouseMutation,
    BulkMoveToTrashMediaHouseMutationVariables
  >(BulkMoveToTrashMediaHouseDocument, {
    refetchQueries: ["GetMediaHouseVideos"],
    onCompleted: (data) => {
      toast.success(
        `Moved ${data.bulkMoveToTrashMediaHouse} ${data.bulkMoveToTrashMediaHouse === 1 ? "item" : "items"} to trash`
      );
      setSelectedMedia(new Map());
    },
    onError: (error) => {
      toast.error(`Failed to delete: ${error.message}`);
    },
  });

  const handleVideoClick = async (mediaHouseMedia: MediaHouseMedia) => {
    await addVideoToSlide(mediaHouseMedia.id);
    setActivePanel(null);
  };

  const handleToggleSelect = (id: string, media: MediaHouseMedia) => {
    setSelectedMedia((prev) => {
      const next = new Map(prev);
      if (next.has(id)) next.delete(id);
      else next.set(id, media);
      return next;
    });
  };

  const handleClearSelection = () => setSelectedMedia(new Map());
  const handleRename = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowRenameModal(true);
  };
  const handleMove = (items: MediaHouseMedia[]) => {
    setItemsToMove(items);
    setShowMoveModal(true);
  };
  const handleDelete = async (items: MediaHouseMedia[]) => {
    if (!confirm(`Move ${items.length} ${items.length === 1 ? "item" : "items"} to trash?`)) return;
    await bulkDelete({ variables: { ids: items.map((item) => item.id) } });
  };
  const handleProperties = (item: MediaHouseMedia) => {
    setActiveMedia(item);
    setShowPropertiesDialog(true);
  };

  const selectedMediaArray = Array.from(selectedMedia.values());
  const selectedIds = new Set(selectedMedia.keys());
  const videos = data?.mediaHouseMedia.edges || [];
  const sortedVideos = [...videos].sort(
    (a, b) => new Date(b.node.createdAt).getTime() - new Date(a.node.createdAt).getTime()
  );
  const hasSelection = selectedMedia.size > 0;
  const pageInfo = data?.mediaHouseMedia?.pageInfo;
  const hasMore = pageInfo?.hasNextPage || false;

  const handleObserver = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [target] = entries;
      if (!target) return;
      if (target.isIntersecting && hasMore && !loading) {
        fetchMore({ variables: { after: pageInfo?.endCursor } });
      }
    },
    [hasMore, loading, fetchMore, pageInfo?.endCursor]
  );

  useEffect(() => {
    const element = observerTarget.current;
    if (!element) return;
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    observer.observe(element);
    return () => observer.disconnect();
  }, [handleObserver]);

  return (
    <div className="pt-2 pb-3 relative">
      <FoldersSection
        parentId={currentFolderId}
        mediaType="video"
        onFolderClick={setCurrentFolderId}
        disabled={disabled}
      />

      <div
        className={`max-h-[calc(100vh-300px)] overflow-y-auto pr-0.5 gradient-scrollbar ${
          disabled ? "opacity-40 blur-[2px] pointer-events-none" : ""
        }`}
      >
        {/* Action Buttons */}
        <div className="flex gap-2 mb-3">
          <Button
            onClick={() => setShowUploadModal(true)}
            variant="secondary"
            size="sm"
            disabled={disabled}
            leftIcon={<FiUploadCloud className="h-3.5 w-3.5" />}
            className="flex-1"
          >
            Upload Video
          </Button>
        </div>

        {/* Video Grid */}
        <div className="grid grid-cols-2 gap-1.5">
          {loading && videos.length === 0 ? (
            Array.from({ length: 6 }).map((_, i) => (
              <div
                // biome-ignore lint/suspicious/noArrayIndexKey: skeleton tiles
                key={i}
                className="aspect-video rounded-xl bg-slate-200 dark:bg-gray-700 animate-pulse"
                style={{ animationDelay: `${i * 60}ms` }}
              />
            ))
          ) : error ? (
            <div
              className="col-span-2 flex flex-col items-center justify-center py-10 px-4 gap-3"
              role="alert"
            >
              <div className="w-12 h-12 rounded-2xl bg-error-50 dark:bg-error-900/30 flex items-center justify-center">
                <FiAlertTriangle className="text-lg text-error-500" />
              </div>
              <p className="text-xs font-semibold text-slate-600 dark:text-gray-300">
                {isServerError(error) ? "Media House unavailable" : "Failed to load"}
              </p>
              <p className="text-[11px] text-slate-400 text-center leading-relaxed">
                {isServerError(error) ? "The server appears to be down." : error.message}
              </p>
              <Button
                onClick={() => refetch()}
                variant="primary"
                size="sm"
                leftIcon={<FiRefreshCw className="h-3 w-3" />}
              >
                Retry
              </Button>
            </div>
          ) : videos.length === 0 ? (
            <div className="col-span-2 flex flex-col items-center justify-center py-12 gap-3">
              <div
                className="w-14 h-14 rounded-2xl flex items-center justify-center"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(99,102,241,0.12) 0%, rgba(20,184,166,0.12) 100%)",
                }}
              >
                <FiFilm className="text-2xl text-slate-400 dark:text-gray-500" />
              </div>
              <p className="text-xs font-semibold text-slate-500 dark:text-gray-400">
                No videos yet
              </p>
              <p className="text-[11px] text-slate-400 dark:text-gray-500 text-center leading-relaxed max-w-[160px]">
                Upload a video to get started
              </p>
            </div>
          ) : (
            sortedVideos.map(({ node: video }: MediaEdge) => {
              const isSelected = selectedIds.has(video.id);
              const isHovered = hoveredId === video.id;
              const showOverlay = isHovered || hasSelection;

              return (
                <Dnd
                  key={video.id}
                  id={video.id}
                  data={{
                    id: video.id,
                    type: "video",
                    media: { src: video.url, filename: video.filename },
                  }}
                >
                  <div
                    className={`relative group cursor-pointer rounded-xl overflow-hidden transition-all duration-150 ${
                      isSelected
                        ? "ring-2 ring-primary-500 ring-offset-1 shadow-lg shadow-primary-500/20"
                        : "hover:shadow-md hover:shadow-black/10"
                    }`}
                    onMouseEnter={() => setHoveredId(video.id)}
                    onMouseLeave={() => setHoveredId(null)}
                    onClick={() => !copying && !isSelected && handleVideoClick(video)}
                  >
                    {/* Thumbnail */}
                    <video
                      src={`${video.url}#t=0.1`}
                      preload="metadata"
                      className="w-full aspect-video object-cover bg-slate-100 dark:bg-gray-800"
                      draggable={false}
                    >
                      <track kind="captions" />
                    </video>

                    {/* Selection overlay tint */}
                    {isSelected && (
                      <div className="absolute inset-0 bg-primary-500/20 pointer-events-none" />
                    )}

                    {/* Hover dark overlay */}
                    {!isSelected && (
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 pointer-events-none transition-colors duration-150" />
                    )}

                    {/* Checkbox */}
                    {showOverlay && (
                      <div className="absolute top-1.5 left-1.5 z-10">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleSelect(video.id, video);
                          }}
                          className={`w-5 h-5 rounded-md flex items-center justify-center transition-all duration-150 shadow-sm ${
                            isSelected
                              ? "bg-primary-500 border-primary-500 border"
                              : "bg-white/90 border border-slate-300 hover:border-primary-400"
                          }`}
                        >
                          {isSelected && <FiCheck className="h-3 w-3 text-white" strokeWidth={3} />}
                        </button>
                      </div>
                    )}

                    {/* Three-dot menu */}
                    {!hasSelection && (
                      <div
                        className="absolute top-1.5 right-1.5 z-10 opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <ThreeDotMenu
                          media={video}
                          onProperties={() => handleProperties(video)}
                          onMove={() => handleMove([video])}
                          onDelete={() => handleDelete([video])}
                        />
                      </div>
                    )}

                    {/* Play preview button */}
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPreviewMedia(video);
                      }}
                      className="absolute inset-0 z-10 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-150"
                      aria-label="Preview video"
                    >
                      <div className="w-9 h-9 rounded-full bg-black/60 backdrop-blur-sm border border-white/20 flex items-center justify-center shadow-lg hover:bg-primary-600 transition-colors">
                        <FiPlay className="h-4 w-4 text-white ml-0.5" />
                      </div>
                    </button>

                    {/* Filename + duration label */}
                    <div
                      className="absolute bottom-0 left-0 right-0 px-2 py-1.5 translate-y-full group-hover:translate-y-0 transition-transform duration-150"
                      style={{
                        background:
                          "linear-gradient(to top, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.4) 60%, transparent 100%)",
                      }}
                    >
                      <div className="flex items-end justify-between gap-1">
                        <p className="text-white text-[10px] font-medium truncate leading-tight">
                          {video.filename}
                        </p>
                        {video.duration && (
                          <span className="flex-shrink-0 text-[10px] font-semibold text-white/80">
                            {Math.round(video.duration)}s
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Copying overlay */}
                    {copying && (
                      <div className="absolute inset-0 bg-white/80 dark:bg-gray-900/80 flex items-center justify-center">
                        <div className="w-5 h-5 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
                      </div>
                    )}
                  </div>
                </Dnd>
              );
            })
          )}

          {hasMore && (
            <div ref={observerTarget} className="col-span-2 h-8 flex items-center justify-center">
              {loading && (
                <div className="w-4 h-4 rounded-full border-2 border-primary-400 border-t-transparent animate-spin" />
              )}
            </div>
          )}
        </div>
      </div>

      <FloatingActionBar
        selectedItems={selectedMediaArray}
        selectedCount={selectedMedia.size}
        mediaType="video"
        onClearSelection={handleClearSelection}
        onRename={handleRename}
        onMove={handleMove}
        onDelete={handleDelete}
      />

      <VideoPreviewModal
        isOpen={!!previewMedia}
        media={previewMedia}
        onClose={() => setPreviewMedia(null)}
      />

      {activeMedia && (
        <>
          <RenameMediaModal
            isOpen={showRenameModal}
            onClose={() => {
              setShowRenameModal(false);
              setActiveMedia(null);
            }}
            mediaId={activeMedia.id}
            currentFilename={activeMedia.filename}
            mediaType="video"
          />
          <MediaPropertiesDialog
            isOpen={showPropertiesDialog}
            onClose={() => {
              setShowPropertiesDialog(false);
              setActiveMedia(null);
            }}
            media={activeMedia}
            mediaType="video"
          />
        </>
      )}

      <MoveMediaModal
        isOpen={showMoveModal}
        onClose={() => {
          setShowMoveModal(false);
          setItemsToMove([]);
        }}
        mediaIds={itemsToMove.map((m) => m.id)}
        mediaType="video"
        currentFolderId={currentFolderId}
      />

      <MediaUploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        mediaType="video"
        folderId={currentFolderId}
        refetchQuery="GetMediaHouseVideos"
      />

      {disabled && (
        <DisabledLockOverlay
          title="Videos Not Available"
          description="This slide type doesn't support video elements"
        />
      )}
    </div>
  );
}
